import { lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { RequireRoles } from './routing/RequireRoles';
import { RequireSurface } from './routing/RequireSurface';
import { RequireStaff } from './routing/RequireStaff';
import { recordReproBreadcrumb } from '@wapharma/shared';
import {
  importCatalogListPage,
  importCatalogPage,
  importHomePage,
  importOrdersPage,
  importProcurementPage,
} from './routing/lazyPages';
import { AdminRouteError } from './components/AdminRouteError';
import { AdminShell } from './AdminShell';
import { BreakUiMenuToggle } from './dev/BreakUiMenuToggle';
import { LoginPage } from './pages/LoginPage';
import { ForcePasswordChangePage } from './pages/ForcePasswordChangePage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';

const HomePage = lazy(() => importHomePage().then((m) => ({ default: m.HomePage })));
const CatalogListPage = lazy(importCatalogListPage);
const CatalogPage = lazy(() => importCatalogPage().then((m) => ({ default: m.CatalogPage })));
const InventoryPage = lazy(() => import('./pages/InventoryPage').then((m) => ({ default: m.InventoryPage })));
const InventoryLotsPage = lazy(() =>
  import('./pages/InventoryLotsPage').then((m) => ({ default: m.InventoryLotsPage })),
);
const InventoryLotDetailPage = lazy(() =>
  import('./pages/InventoryLotDetailPage').then((m) => ({ default: m.InventoryLotDetailPage })),
);
const LocationsPage = lazy(() => import('./pages/LocationsPage').then((m) => ({ default: m.LocationsPage })));
const ProductTypesPage = lazy(() =>
  import('./pages/ProductTypesPage').then((m) => ({ default: m.ProductTypesPage })),
);
const ProductTypeDetailPage = lazy(() =>
  import('./pages/ProductTypeDetailPage').then((m) => ({ default: m.ProductTypeDetailPage })),
);
const OptionGroupDetailPage = lazy(() =>
  import('./pages/OptionGroupDetailPage').then((m) => ({ default: m.OptionGroupDetailPage })),
);
const OrdersPage = lazy(() => importOrdersPage().then((m) => ({ default: m.OrdersPage })));
const ProcurementPage = lazy(() => importProcurementPage().then((m) => ({ default: m.ProcurementPage })));
const QuotesPage = lazy(() => import('./pages/QuotesPage').then((m) => ({ default: m.QuotesPage })));
const SaleQuotesPage = lazy(() =>
  import('./pages/SaleQuotesPage').then((m) => ({ default: m.SaleQuotesPage })),
);
const CustomerPage = lazy(() => import('./pages/CustomerPage').then((m) => ({ default: m.CustomerPage })));
const B2bLegacyRedirect = lazy(() =>
  import('./pages/B2bLegacyRedirect').then((m) => ({ default: m.B2bLegacyRedirect })),
);
const DocumentsPage = lazy(() =>
  import('./pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const SearchPage = lazy(() => import('./pages/SearchPage').then((m) => ({ default: m.SearchPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const RoleDetailPage = lazy(() => import('./pages/RoleDetailPage').then((m) => ({ default: m.RoleDetailPage })));
const FulfillmentSettingsPage = lazy(() =>
  import('./pages/FulfillmentSettingsPage').then((m) => ({ default: m.FulfillmentSettingsPage })),
);
const OpenSettingsDataSources = lazy(() =>
  import('./pages/OpenSettingsDataSources').then((m) => ({
    default: m.OpenSettingsDataSources,
  })),
);
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const LeadsPage = lazy(() => import('./pages/LeadsPage').then((m) => ({ default: m.LeadsPage })));
const ConventionsPage = lazy(() =>
  import('./pages/ConventionsPage').then((m) => ({ default: m.ConventionsPage })),
);
const ConventionDetailPage = lazy(() =>
  import('./pages/conventions/ConventionDetailPage').then((m) => ({
    default: m.ConventionDetailPage,
  })),
);
const ConventionTemplatesPage = lazy(() =>
  import('./pages/conventions/ConventionTemplatesPage').then((m) => ({
    default: m.ConventionTemplatesPage,
  })),
);
const ConventionTemplateEditorPage = lazy(() =>
  import('./pages/conventions/ConventionTemplateEditorPage').then((m) => ({
    default: m.ConventionTemplateEditorPage,
  })),
);

/** Old `/catalog/legacy` bookmarks → new list route (query preserved). */
function CatalogLegacyRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={q ? `/catalog?${q}` : '/catalog'} replace />;
}
const DesignSystemPlaygroundPage = lazy(() =>
  import('./pages/DesignSystemPlaygroundPage').then((m) => ({ default: m.DesignSystemPlaygroundPage })),
);
const ConventionLetterheadPlaygroundPage = lazy(() =>
  import('./pages/ConventionLetterheadPlaygroundPage').then((m) => ({
    default: m.ConventionLetterheadPlaygroundPage,
  })),
);
const SupportTicketsPage = lazy(() =>
  import('./pages/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage })),
);
const AccountingPage = lazy(() =>
  import('./pages/AccountingPage').then((m) => ({ default: m.AccountingPage })),
);
const StoragePage = lazy(() => import('./pages/StoragePage').then((m) => ({ default: m.StoragePage })));

function captureUiBreadcrumbFromEvent(e: Event): void {
  if (typeof window === 'undefined') return;
  const target = e.target;
  if (!(target instanceof HTMLElement)) return;
  const tag = target.tagName.toLowerCase();
  if (tag === 'html' || tag === 'body') return;

  const el = target.closest('a,button,[role=\"button\"],[data-testid],input,select,textarea') ?? target;
  if (!(el instanceof HTMLElement)) return;

  const label =
    el.getAttribute('aria-label')?.trim() ||
    el.getAttribute('title')?.trim() ||
    el.textContent?.trim().replace(/\\s+/g, ' ').slice(0, 120) ||
    tag;
  if (!label) return;

  const testId = el.getAttribute('data-testid')?.trim();
  const id = el.id?.trim();
  const role = el.getAttribute('role')?.trim() || undefined;
  const href =
    el instanceof HTMLAnchorElement ? el.getAttribute('href')?.trim() || undefined : undefined;
  const selector = testId
    ? `[data-testid=\"${testId}\"]`
    : id
      ? `#${id}`
      : role
        ? `${el.tagName.toLowerCase()}[role=\"${role}\"]`
        : el.tagName.toLowerCase();

  recordReproBreadcrumb({ t: 'ui', label, selector, href, role });
}

export function App() {
  const location = useLocation();

  useEffect(() => {
    recordReproBreadcrumb({ t: 'route', path: location.pathname + location.search });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const captureOpts: AddEventListenerOptions = { capture: true };
    const onClick = (e: MouseEvent) => captureUiBreadcrumbFromEvent(e);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      captureUiBreadcrumbFromEvent(e);
    };
    window.addEventListener('click', onClick, captureOpts);
    window.addEventListener('keydown', onKeyDown, captureOpts);
    return () => {
      window.removeEventListener('click', onClick, captureOpts);
      window.removeEventListener('keydown', onKeyDown, captureOpts);
    };
  }, []);

  return (
    <AuthProvider>
      <ToastProvider>
        <>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/force-password-change" element={<ForcePasswordChangePage />} />
            <Route element={<AdminShell />} errorElement={<AdminRouteError />}>
              <Route element={<RequireStaff />}>
                <Route index element={<HomePage />} />
                <Route element={<RequireSurface surface="seeCatalog" />}>
                  <Route path="catalog/products/:productId" element={<CatalogPage />} />
                  <Route path="catalog/legacy" element={<CatalogLegacyRedirect />} />
                  <Route path="catalog/types/:typeCode" element={<ProductTypeDetailPage />} />
                  <Route path="catalog/types" element={<ProductTypesPage />} />
                  <Route path="catalog/option-groups/:groupId" element={<OptionGroupDetailPage />} />
                  <Route path="catalog" element={<CatalogListPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeOrders" />}>
                  <Route path="orders/:orderId?" element={<OrdersPage />} />
                  <Route path="sales/quotes/:id" element={<SaleQuotesPage />} />
                  <Route path="sales/quotes" element={<SaleQuotesPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeDocuments" />}>
                  <Route path="documents" element={<DocumentsPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeSearch" />}>
                  <Route path="search" element={<SearchPage />} />
                </Route>
                <Route path="b2b/clients/:clientId" element={<B2bLegacyRedirect mode="client" />} />
                <Route path="b2b/sales-orders/:salesOrderId" element={<B2bLegacyRedirect mode="order" />} />
                <Route path="b2b" element={<B2bLegacyRedirect mode="hub" />} />
                <Route path="stock" element={<Navigate to="/inventory" replace />} />
                <Route path="stock/*" element={<Navigate to="/inventory" replace />} />
                <Route element={<RequireSurface surface="seeInventory" />}>
                  <Route path="inventory/locations" element={<LocationsPage />} />
                  <Route path="inventory/lots/:batchId" element={<InventoryLotDetailPage />} />
                  <Route path="inventory/lots" element={<InventoryLotsPage />} />
                  <Route path="inventory" element={<InventoryPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeProcurement" />}>
                  <Route path="procurement/quotes/:quoteId" element={<QuotesPage />} />
                  <Route path="procurement/quotes" element={<QuotesPage />} />
                  <Route path="procurement/purchase-orders/:purchaseOrderId" element={<ProcurementPage />} />
                  <Route path="procurement/purchase-orders" element={<ProcurementPage />} />
                  <Route path="procurement/suppliers/:supplierId" element={<ProcurementPage />} />
                  <Route path="procurement/suppliers" element={<ProcurementPage />} />
                  <Route path="procurement/payments" element={<Navigate to="/procurement/suppliers" replace />} />
                  <Route path="procurement" element={<Navigate to="/procurement/purchase-orders" replace />} />
                </Route>
                <Route element={<RequireSurface surface="seeConventions" />}>
                  <Route path="conventions/templates/:templateId" element={<ConventionTemplateEditorPage />} />
                  <Route path="conventions/templates" element={<ConventionTemplatesPage />} />
                  <Route path="conventions/:envelopeId" element={<ConventionDetailPage />} />
                  <Route path="conventions" element={<ConventionsPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeCustomers" />}>
                  <Route path="customer/demands/:demandId" element={<Navigate to="/leads" replace />} />
                  <Route path="customer/demands" element={<Navigate to="/leads" replace />} />
                  <Route path="customer/profiles/:profileId" element={<CustomerPage />} />
                  <Route path="customer/legal-entities/:legalEntityId" element={<CustomerPage />} />
                  <Route
                    path="customer/pro/:proClientId/orders/:salesOrderId"
                    element={<CustomerPage />}
                  />
                  <Route path="customer/pro/:proClientId" element={<CustomerPage />} />
                  <Route path="customer" element={<CustomerPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeLeads" />}>
                  <Route path="crm/flows/:id" element={<Navigate to="/leads" replace />} />
                  <Route path="crm/flows" element={<Navigate to="/leads" replace />} />
                  <Route path="leads/:leadId" element={<LeadsPage />} />
                  <Route path="leads" element={<LeadsPage />} />
                  <Route path="settings/fulfillment" element={<FulfillmentSettingsPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeSupport" />}>
                  <Route path="support/:ticketId" element={<SupportTicketsPage />} />
                  <Route path="support" element={<SupportTicketsPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeDataSources" />}>
                  <Route path="settings/data-sources" element={<OpenSettingsDataSources />} />
                </Route>
                <Route element={<RequireSurface surface="seeAccounting" />}>
                  <Route path="accounting" element={<AccountingPage />} />
                </Route>
                <Route element={<RequireSurface surface="seeStorage" />}>
                  <Route path="storage" element={<StoragePage />} />
                </Route>
                <Route element={<RequireSurface surface="seeParty" />}>
                  <Route path="users/:userId" element={<UsersPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="roles/:roleId" element={<RoleDetailPage />} />
                  <Route path="roles" element={<RolesPage />} />
                </Route>
                <Route element={<RequireRoles anyOf={['SUPER_ADMIN']} />}>
                  <Route path="playground" element={<DesignSystemPlaygroundPage />} />
                  <Route
                    path="playground/convention-letterhead"
                    element={<ConventionLetterheadPlaygroundPage />}
                  />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
          {import.meta.env.DEV ? <BreakUiMenuToggle /> : null}
        </>
      </ToastProvider>
    </AuthProvider>
  );
}
